import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';

import {
    MaterialAlertComponent,
    MaterialItemListComponent,
    MaterialSpinnerComponent,
    PhoneNumberUtil,
} from '@ecm/material';
import { Contract, PortfolioService, UrlHelper, Usage, UsageHelper } from '@ecm/portfolio';
import { KenobiService, Status, TMSService } from '@ecm/tracker';

import { ErrorPageService } from '../../../services/error-page.service';
import { RouterService } from '../../../services/router.service';
import { AbstractSelectorComponent } from '../abstract-selector.component';
import { SelectorReboundLinksComponent } from '../shared/rebound-links/selector-rebound-links.component';
import { SelectorIdTrackingEnum } from '../shared/selector-id-tracking.enum';
import { SelectorUniverseEnum } from '../shared/selector-universe.enum';

@Component({
    selector: 'ecm-selector-usage',
    templateUrl: 'selector-usage.html',
    standalone: true,
    imports: [
        NgClass,
        MaterialItemListComponent,
        MaterialAlertComponent,
        SelectorReboundLinksComponent,
        MaterialSpinnerComponent,
        TranslateModule,
    ],
})
export class SelectorUsageComponent extends AbstractSelectorComponent implements OnInit {
    public itemsRender = new Map<string, { icon: string; label: string; description: string[] }>();

    public constructor(
        protected activatedRoute: ActivatedRoute,
        protected errorPageService: ErrorPageService,
        protected routerService: RouterService,
        protected portfolioService: PortfolioService,
        protected kenobiService: KenobiService,
        protected tmsService: TMSService,
        private translateParser: TranslateParser,
        private translateService: TranslateService,
    ) {
        super(activatedRoute, errorPageService, routerService, portfolioService, kenobiService, tmsService);

        this.universe = SelectorUniverseEnum.USAGE;
        this.idTracking = SelectorIdTrackingEnum.USAGE;
    }

    /**
     * @inheritDoc
     */
    public ngOnInit() {
        super.init();

        if (!this.isAuthorizedUsageQueryParamValid()) {
            // authorizedUsage query param is not valid
            this.errorPageService.showDefaultErrorPage();
        }

        this.portfolioService
            .getContractsHavingUsage(this.activatedRoute.snapshot.queryParams.authorizedUsage)
            .subscribe({
                next: (availableContracts: Contract[]) => {
                    // filtered available contracts with return url cid value if exist
                    const filteredAvailableContracts = availableContracts.filter(
                        (contract: Contract) =>
                            contract.cid ===
                            UrlHelper.matchCid(this.activatedRoute.snapshot.queryParams.returnUrl)?.[1],
                    );

                    if (filteredAvailableContracts.length > 0) {
                        availableContracts = filteredAvailableContracts;
                    }

                    if (availableContracts.length === 1 && availableContracts[0].usages.length === 1) {
                        // only one equipment is available
                        this.redirectToReturnUrl(availableContracts[0], availableContracts[0].usages[0].usageCode);
                    } else {
                        this.groupContractsByBrand(availableContracts);
                        this.computeItemsRender(availableContracts);

                        // tracking
                        this.onAfterViewLoaded(availableContracts.length === 0 ? Status.KORG : Status.OK, 'SELUSAGE');
                    }
                },
                error: () => {
                    // tracking
                    this.onAfterViewLoaded(Status.KOSI, 'SELUSAGE');

                    this.errorPageService.showDefaultErrorPage();
                },
            });
    }

    /**
     * Check validity of authorizedUsage query param
     * @returns {boolean}
     */
    private isAuthorizedUsageQueryParamValid(): boolean {
        if (!Object.prototype.hasOwnProperty.call(this.activatedRoute.snapshot.queryParams, 'authorizedUsage')) {
            return false;
        }

        const authorizedUsage: string | string[] = this.activatedRoute.snapshot.queryParams.authorizedUsage;

        return (typeof authorizedUsage === 'string' ? [authorizedUsage] : authorizedUsage).every((usage: string) =>
            UsageHelper.isUsageWellFormatted(UsageHelper.extractUsageCodeFromUsage(usage)),
        );
    }

    /**
     * Compute items list displayed information
     * @param {Contract[]} contracts
     * @private
     */
    protected computeItemsRender(contracts: Contract[]): void {
        contracts.forEach((contract: Contract) => {
            contract?.usages?.forEach((usage: Usage) => {
                // get associated usage translation
                const translation = this.translateService.instant(
                    `ecm.selector.usage.${UsageHelper.extractUsageCodeFromUsage(usage.usageCode)}`,
                );
                const personalizedName = contract.associatedFamilyMembers?.[0]?.firstName;

                let label =
                    translation.label?.[UsageHelper.isPrimary(usage.usageCode) ? 'primary' : 'secondary'] ||
                    translation.label?.[personalizedName ? 'personalized' : 'default'] ||
                    translation.label;

                if (usage.usageCommercialLabel) {
                    label = usage.usageCommercialLabel;
                }

                // compute item description
                const description = Array.isArray(translation.desc) ? translation.desc : [translation.desc];

                this.itemsRender.set(`${contract.cid}_${usage.usageCode}`, {
                    icon: translation.icon,
                    label: this.translateParser.interpolate(label, {
                        lineNumber: PhoneNumberUtil.getFormattedPhone(usage.usageLine),
                        name: personalizedName || '',
                    }),
                    description: description
                        .map((item: string) =>
                            this.translateParser.interpolate(item, {
                                lineNumber: PhoneNumberUtil.getFormattedPhone(usage.usageLine),
                                cityName: contract?.contractInstallationArea?.city || '',
                                usageBrand: usage?.usageBrand || '',
                                usageModel: usage?.usageModel || '',
                            }),
                        )
                        // filter description (it's to prevent empty brand and model on mobile usage)
                        .filter((item: string) => item?.length > 0 && item !== ' '),
                });
            });
        });
    }
}
